<script setup>
import { computed } from 'vue'

// props 是父组件传下来的只读数据（单向数据流）。
const props = defineProps({
  poll: { type: Object, required: true },
  pending: { type: Boolean, default: false }
})

// emits 把"用户想做什么"抛给父组件，子组件不直接改数据。
const emit = defineEmits(['vote', 'remove'])

// computed 依赖变化时自动重算，并带缓存。
const totalVotes = computed(() =>
  props.poll.options.reduce((sum, option) => sum + option.votes, 0)
)

const leaderVotes = computed(() =>
  Math.max(0, ...props.poll.options.map((option) => option.votes))
)

function percent(votes) {
  if (totalVotes.value === 0) return 0
  return Math.round((votes / totalVotes.value) * 100)
}
</script>

<template>
  <article class="card" :class="{ busy: pending }" :aria-busy="pending">
    <header>
      <h3>{{ poll.title }}</h3>
      <button class="ghost" aria-label="删除该投票" @click="emit('remove', poll.id)">✕</button>
    </header>

    <ul>
      <li v-for="option in poll.options" :key="option.id">
        <button
          class="option"
          :class="{ chosen: option.id === poll.votedOptionId }"
          :aria-pressed="option.id === poll.votedOptionId"
          :disabled="pending"
          @click="emit('vote', { pollId: poll.id, optionId: option.id })"
        >
          <span class="label">
            <span class="text">{{ option.text }}</span>
            <span class="count">
              {{ option.votes }} 票 · {{ percent(option.votes) }}%
              <span v-if="option.votes === leaderVotes && totalVotes > 0" aria-hidden="true">👑</span>
            </span>
          </span>
          <span class="bar" aria-hidden="true">
            <span class="fill" :style="{ width: percent(option.votes) + '%' }" />
          </span>
        </button>
      </li>
    </ul>

    <footer>
      <span>共 {{ totalVotes }} 票</span>
      <span v-if="poll.votedOptionId">已投票，可点其他选项改票</span>
      <span v-else>还没投票</span>
    </footer>
  </article>
</template>

<style scoped>
.card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 18px;
  transition: opacity 0.2s;
}

.card.busy { opacity: 0.6; }

header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

h3 { margin: 0 0 12px; font-size: 17px; }

.ghost { padding: 2px 8px; color: var(--muted); background: transparent; border-color: transparent; }
.ghost:hover { color: #ff7b72; }

ul { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px; }

.option {
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  background: #12151b;
  display: grid;
  gap: 8px;
}

.option.chosen { border-color: var(--accent); background: #16233d; }

.label { display: flex; justify-content: space-between; gap: 12px; font-size: 14px; }
.count { color: var(--muted); white-space: nowrap; }

.bar { display: block; height: 6px; border-radius: 999px; background: #232833; overflow: hidden; }
.fill { display: block; height: 100%; background: var(--accent); transition: width 0.35s ease; }

footer {
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: var(--muted);
}
</style>
